import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import TopNavigator from "../components/TopNavigator";
import { apiUrl } from "../config/api";
import { userAuthHeaders } from "../utils/authHeaders";
import { formatDisplayNumber, formatNumberInput, parseFormattedNumber } from "../utils/numberFormat";

const inputStyle = {
  width: "100%",
  backgroundColor: "#121212",
  color: "#fff",
  border: "1px solid rgba(255,255,255,0.12)",
  borderRadius: "12px",
  padding: "12px 14px",
  outline: "none",
};

const tableHeadStyle = {
  padding: "12px",
  textAlign: "right",
  fontWeight: "bold",
};

const tableCellStyle = {
  padding: "14px 12px",
  textAlign: "right",
  borderTop: "1px solid rgba(255,255,255,0.08)",
};

function TransferPage() {
  const [form, setForm] = useState({
    amount: "",
    unit: "gram",
    receiverName: "",
    personId: "",
  });
  const [people, setPeople] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const availablePeople = useMemo(
    () => people.filter((person) => person.role === "receiver" || person.role === "both"),
    [people]
  );

  const loadPeople = useCallback(async () => {
    const response = await fetch(apiUrl("/api/transfers/people"), {
      headers: userAuthHeaders(),
    });
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "خطا در دریافت لیست اشخاص");
    }

    setPeople(data.people || []);
  }, []);

  const loadRequests = useCallback(async () => {
    const response = await fetch(apiUrl("/api/transfers?limit=100"), {
      headers: userAuthHeaders(),
    });
    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.message || "خطا در دریافت لیست حواله‌ها");
    }

    setRequests(data.transfers || []);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        await Promise.all([loadPeople(), loadRequests()]);
      } catch (error) {
        toast.error(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [loadPeople, loadRequests]);

  const formatDate = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("fa-IR");
  };

  const getUnitLabel = (unit) => (unit === "gram" ? "گرم" : "ریال");

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved":
        return "تایید شده";
      case "rejected":
        return "رد شده";
      case "pending":
      default:
        return "در انتظار بررسی";
    }
  };

  const handleChange = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const amount = parseFormattedNumber(form.amount, { allowDecimal: true });
    const receiverName = form.receiverName.trim();
    const selectedPerson = availablePeople.find((person) => String(person.id) === String(form.personId));

    if (!amount || Number(amount) <= 0 || !receiverName || !selectedPerson) {
      toast.error("همه فیلدهای حواله را کامل کنید.");
      return;
    }

    try {
      const response = await fetch(apiUrl("/api/transfers"), {
        method: "POST",
        headers: userAuthHeaders({ "Content-Type": "application/json" }),
        body: JSON.stringify({
          amount,
          unit: form.unit,
          receiverName,
          personId: selectedPerson.id,
        }),
      });
      const data = await response.json();

      if (!response.ok || !data.success) {
        throw new Error(data.message || "خطا در ثبت درخواست حواله");
      }

      toast.success("درخواست حواله ثبت شد.");
      setRequests((prev) => [data.transfer, ...prev]);
      setForm({
        amount: "",
        unit: "gram",
        receiverName: "",
        personId: "",
      });
    } catch (error) {
      toast.error(error.message);
    }
  };

  return (
    <div className="MainPageShell">
      <TopNavigator />
      <main className="container UserPortalPage">
        <section className="UserPortalHero">
          <span className="MainPageEyebrow">پرتال کاربر</span>
          <h2 className="MainPageTitle">ثبت حواله</h2>
          <p className="MainPageDescription">
            درخواست حواله را ثبت کنید. بعد از بررسی و تایید، وضعیت آن در همین صفحه قابل پیگیری است.
          </p>
        </section>

        <section className="UserPortalPanel">
          <form onSubmit={handleSubmit} className="TransferForm">
            <div className="TransferFormGrid">
              <label style={{ display: "grid", gap: "8px" }}>
                <span>مقدار حواله</span>
                <input
                  type="text"
                  value={form.amount}
                  onChange={(event) =>
                    handleChange("amount", formatNumberInput(event.target.value, { allowDecimal: true }))
                  }
                  placeholder="مثلاً 250"
                  style={inputStyle}
                />
              </label>

              <label style={{ display: "grid", gap: "8px" }}>
                <span>واحد</span>
                <select
                  value={form.unit}
                  onChange={(event) => handleChange("unit", event.target.value)}
                  style={inputStyle}
                >
                  <option value="gram">گرم</option>
                  <option value="toman">ریال</option>
                </select>
              </label>
            </div>

            <label style={{ display: "grid", gap: "8px" }}>
              <span>نام گیرنده حواله</span>
              <input
                type="text"
                value={form.receiverName}
                onChange={(event) => handleChange("receiverName", event.target.value)}
                placeholder="نام گیرنده را وارد کنید"
                style={inputStyle}
              />
            </label>

            <label style={{ display: "grid", gap: "8px" }}>
              <span>نزد</span>
              <select
                value={form.personId}
                onChange={(event) => handleChange("personId", event.target.value)}
                style={inputStyle}
              >
                <option value="">انتخاب شخص</option>
                {availablePeople.map((person) => (
                  <option key={person.id} value={person.id}>
                    {person.name}
                  </option>
                ))}
              </select>
            </label>

            <div className="TransferFormActions">
              <button
                type="submit"
                style={{
                  border: "none",
                  borderRadius: "12px",
                  padding: "12px 18px",
                  backgroundColor: "#ffcc00",
                  color: "#000",
                  fontWeight: "bold",
                  cursor: "pointer",
                }}
              >
                ثبت درخواست حواله
              </button>
            </div>
          </form>

          <div
            style={{
              marginTop: "18px",
              backgroundColor: "rgba(255, 204, 0, 0.08)",
              border: "1px solid rgba(255, 204, 0, 0.22)",
              borderRadius: "14px",
              padding: "14px 16px",
              color: "#f5df8f",
              lineHeight: 1.9,
            }}
          >
            ثبت درخواست حواله به منزله انجام آن نیست و پس از بررسی تایید جا به جایی صورت خواهد گرفت.
          </div>

          <div style={{ marginTop: "28px" }}>
            <div className="MainPageSectionHeading" style={{ marginBottom: "14px" }}>
              <span className="MainPagePanelEyebrow">پیگیری</span>
              <h3 className="MainPagePanelTitle">لیست حواله های ثبت شده</h3>
            </div>

            {loading ? (
              <p className="UserPortalEmpty">در حال دریافت اطلاعات...</p>
            ) : availablePeople.length === 0 ? (
              <p className="UserPortalEmpty" style={{ marginBottom: "16px" }}>
                هنوز شخصی برای انتخاب در بخش نزد تعریف نشده است.
              </p>
            ) : null}

            {!loading && requests.length === 0 ? (
              <p className="UserPortalEmpty">هنوز حواله‌ای ثبت نشده است.</p>
            ) : !loading ? (
              <>
                <div className="UserPortalTableWrap TredeDetailDesktopTable">
                  <table className="UserPortalTable">
                    <thead>
                      <tr>
                        <th style={tableHeadStyle}>#</th>
                        <th style={tableHeadStyle}>مقدار حواله</th>
                        <th style={tableHeadStyle}>نام گیرنده</th>
                        <th style={tableHeadStyle}>نزد</th>
                        <th style={tableHeadStyle}>وضعیت</th>
                        <th style={tableHeadStyle}>تاریخ</th>
                      </tr>
                    </thead>
                    <tbody>
                      {requests.map((request, index) => (
                        <tr key={request.id}>
                          <td style={tableCellStyle} data-label="#">
                            {index + 1}
                          </td>
                          <td style={tableCellStyle} data-label="مقدار حواله">
                            {formatDisplayNumber(request.amount)} {getUnitLabel(request.unit)}
                          </td>
                          <td style={tableCellStyle} data-label="نام گیرنده">
                            {request.receiverName}
                          </td>
                          <td style={tableCellStyle} data-label="نزد">
                            {request.personName || "-"}
                          </td>
                          <td style={tableCellStyle} data-label="وضعیت">
                            <span
                              className={
                                request.status === "approved"
                                  ? "TredeDetailBadge approved"
                                  : request.status === "rejected"
                                    ? "TredeDetailBadge rejected"
                                    : "TredeDetailBadge pending"
                              }
                            >
                              {getStatusLabel(request.status)}
                            </span>
                          </td>
                          <td style={tableCellStyle} data-label="تاریخ">
                            {formatDate(request.createdAt)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div className="TredeDetailMobileCards">
                  {requests.map((request) => {
                    const statusIcon =
                      request.status === "approved" ? "✓" : request.status === "rejected" ? "×" : "•";

                    return (
                      <article key={request.id} className="TredeDetailMobileCard">
                        <div className="TredeDetailMobileTop">
                          <div className="TredeDetailMobileMain">
                            <span
                              className={
                                request.status === "approved"
                                  ? "TredeDetailMobileStatus approved"
                                  : request.status === "rejected"
                                    ? "TredeDetailMobileStatus rejected"
                                    : "TredeDetailMobileStatus pending"
                              }
                            >
                              {statusIcon}
                            </span>
                            <div className="TredeDetailMobileTypeWrap">
                              <strong className="TredeDetailMobileType">{request.receiverName}</strong>
                              <span className="TredeDetailMobileRate">
                                نزد {request.personName || "-"}
                              </span>
                            </div>
                          </div>

                          <div className="TredeDetailMobileValues">
                            <div>
                              <span>مقدار</span>
                              <strong>
                                {formatDisplayNumber(request.amount)} {getUnitLabel(request.unit)}
                              </strong>
                            </div>
                            <div>
                              <span>وضعیت</span>
                              <strong>{getStatusLabel(request.status)}</strong>
                            </div>
                          </div>
                        </div>

                        <div className="TredeDetailMobileBottom">
                          <span className="TredeDetailMobileDate">{formatDate(request.createdAt)}</span>
                        </div>
                      </article>
                    );
                  })}
                </div>
              </>
            ) : null}
          </div>
        </section>
      </main>
    </div>
  );
}

export default TransferPage;
